import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import PromoBanner from "../../components/PromoBanner";
import { Send, ArrowLeft, Hash } from "lucide-react";
import { toast } from "sonner";

const POST_TYPES = [
  { id: "text", label: "Text" },
  { id: "chart", label: "Chart" },
  { id: "question", label: "Question" },
  { id: "dd", label: "DD" },
  { id: "news", label: "News" },
  { id: "trade_review", label: "Trade Review" },
  { id: "educational", label: "Educational" },
  { id: "idea", label: "Idea" },
  { id: "meme", label: "Meme" },
];

export default function NewPost() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [communities, setCommunities] = useState([]);
  const [form, setForm] = useState({
    community_slug: params.get("community") || "stocks",
    title: "",
    body: "",
    post_type: "text",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/api/community/communities").then(({ data }) => setCommunities(data.communities || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (form.title.trim().length < 3) { toast.error("Title too short"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/api/community/posts", form);
      toast.success(data.mod_status === "reduced_visibility"
        ? "Posted (reduced visibility while reviewed)"
        : "Posted!");
      nav(`/community/post/${data.id}`);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d?.message || d || "Post failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen flex flex-col" data-testid="new-post-page">
      <Header />
      <PromoBanner />
      <main id="main-content" className="flex-1 max-w-[800px] w-full mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => nav(-1)} className="btn-ghost inline-flex items-center gap-1 mb-3 text-[12px]">
          <ArrowLeft className="w-3 h-3" /> Back
        </button>
        <div className="card-surface p-5">
          <h1 className="text-2xl text-white font-semibold tracking-tight mb-4">New post</h1>
          <div className="space-y-3">
            <div>
              <label className="label-mono">Community</label>
              <select value={form.community_slug}
                      onChange={(e) => setForm({ ...form, community_slug: e.target.value })}
                      className="input-glass w-full mt-1" data-testid="community-select">
                {communities.map((c) => (
                  <option key={c.slug} value={c.slug}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-mono">Post type</label>
              <div className="flex flex-wrap gap-1 mt-1">
                {POST_TYPES.map((p) => (
                  <button key={p.id} onClick={() => setForm({ ...form, post_type: p.id })}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-mono uppercase tracking-wider border ${
                            form.post_type === p.id
                              ? "bg-white/[0.08] border-white/30 text-white"
                              : "bg-white/[0.02] border-white/10 text-white/60 hover:text-white"
                          }`}
                          data-testid={`type-${p.id}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="label-mono">Title</label>
              <input value={form.title}
                     onChange={(e) => setForm({ ...form, title: e.target.value })}
                     placeholder="e.g. NVDA breaking out on volume"
                     className="input-glass w-full mt-1" maxLength={200}
                     data-testid="title-input"/>
            </div>
            <div>
              <label className="label-mono">Body</label>
              <textarea value={form.body}
                        onChange={(e) => setForm({ ...form, body: e.target.value })}
                        placeholder="Share your analysis, chart, or setup. Tag tickers with $AAPL — they'll auto-link."
                        rows={8}
                        className="w-full mt-1 input-glass"
                        maxLength={10000}
                        data-testid="body-input"/>
            </div>
            <div className="text-[11px] text-white/50 flex items-start gap-1.5 leading-relaxed">
              <Hash className="w-3 h-3 mt-0.5 shrink-0" />
              <span>Rules: no pump-and-dump coordination, no scams/phishing, no impersonation, no affiliate spam.
                Posts are auto-scored — clear violations are blocked, borderline cases get reduced visibility.</span>
            </div>
            <div className="flex justify-end">
              <button onClick={submit} disabled={busy || form.title.trim().length < 3}
                      className="btn-accent inline-flex items-center gap-2"
                      data-testid="submit-post-btn">
                <Send className="w-3.5 h-3.5" /> {busy ? "Posting…" : "Post"}
              </button>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
